import { Alert, Badge, Card, Skeleton, Space, Tooltip, Typography, theme } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { EmptyState } from '@/shared/ui/EmptyState'
import { fetchNasHealth } from '@/features/nas/nasApi'
import { relativeFromNow } from '@/features/session/relativeTime'

/**
 * Tarmoq holati — har NAS tirikmi.
 *
 * <h2>Nega bosh sahifada</h2>
 * "Internet ishlamayapti" degan qo'ng'iroq kelganda birinchi savol —
 * MUAMMO BIZDAMI yoki bitta abonentdami. Bu ma'lumot allaqachon bor edi
 * (`GET /api/nas/health`), lekin faqat NAS sahifasida — ya'ni operator
 * uni ATAYLAB izlaganda topardi, va aynan shoshilinch paytda.
 *
 * <h2>⚠️ "Tirik" ta'rifi — accounting paketi bo'yicha</h2>
 * Bu ping emas: NAS oxirgi marta qachon hisobot yuborganini ko'rsatadi.
 * Ya'ni "javob beryapti" emas, "ishlayapti va bizga yozyapti" — billing
 * uchun aynan ikkinchisi muhim. Qurilma tirik bo'lib, hisobot
 * yubormasa, trafik hisobga olinmaydi va bu ham nosozlik.
 */
export function NetworkHealthPanel() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const allowed = can('nas.read')
  /*
    ⚠️ "Qurilma qo'shish" tugmasi HUQUQQA qarab chiziladi.

    `nas.read` bor, `nas.create` yo'q operator (masalan qo'llab-quvvatlash
    xodimi) uchun tugma ochiladigan sahifada baribir hech narsa qila
    olmasdi — ya'ni bo'sh holat uni "qil" deb chaqirib, keyin "ruxsat
    yo'q" deb qaytarardi. Bunday holatda faqat tushuntirish qoladi.
  */
  const canAdd = can('nas.create')

  const health = useQuery({
    queryKey: queryKeys.nas.health,
    queryFn: ({ signal }) => fetchNasHealth(signal),
    enabled: allowed,
    refetchInterval: 30_000,
  })

  if (!allowed) return null

  const rows = health.data?.rows ?? []
  /*
    ⚠️ "Muammo" = SILENT yoki NO_DATA.

    `IDLE` — qurilma ishlayapti, shunchaki sessiya yo'q (tunda odatiy).
    Uni qizil qilish har kecha yolg'on tashvish berardi va operator
    ranglarga ishonishni to'xtatardi. `UNMATCHED` esa sozlama
    nomuvofiqligi — u ham e'tibor talab qiladi.
  */
  const problem = (state: string) => state === 'SILENT' || state === 'NO_DATA' || state === 'UNMATCHED'
  const down = rows.filter((d) => problem(d.state)).length

  return (
    <Card
      title={
        <Space size={8}>
          <span>{t('dashboard.network.title')}</span>
          {/* ⚠️ Sarlavhada SON: karta yig'ilgan holatda ham muammo
              borligi ko'rinib tursin */}
          {down > 0 ? (
            <Badge count={down} style={{ backgroundColor: token.colorError }} />
          ) : null}
        </Space>
      }
      extra={<Link to="/nas">{t('dashboard.network.all')}</Link>}
      size="small"
      style={{ height: '100%' }}
    >
      {health.isError ? (
        <Alert type="error" showIcon message={translateError(health.error, t)} />
      ) : health.isPending ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : rows.length === 0 ? (
        /*
          ⚠️ X-SIB-020 — BU YERDA QIZIL ALERT TURARDI VA U NOTO'G'RI EDI.

          Eski mulohaza shunday edi: qurilma yozuvi yo'q bo'lsa FreeRADIUS
          hech bir klientni tanimaydi va har paketni jimgina tashlaydi,
          demak bu halokat. Faktning o'zi to'g'ri, lekin XULOSA noto'g'ri
          — u BITTA holatni (ishlab turgan tarmoq, yozuv tasodifan
          o'chirilgan) hisobga olib, IKKINCHISINI (hali hech narsa
          sozlanmagan yangi tashkilot) e'tibordan qochirgan.

          Amalda ikkinchisi ko'proq uchraydi: har bir yangi tenant
          hayotini AYNAN shu holatdan boshlaydi. Ular bosh sahifani
          birinchi marta ochganda avariya bannerini ko'rardi — go'yo
          o'zlari hali qilishga ulgurmagan ishda nimadir buzilgandek.

          Ikki holatni farqlashning ISHONCHLI usuli panelda YO'Q: bo'sh
          reyestr ikkalasida ham bir xil ko'rinadi. Shuning uchun qoida
          teskari tomondan qo'llanadi — QIZIL FAQAT ISBOTLANGAN
          NOSOZLIK UCHUN. Bu yerdagi ma'lumot esa "hali qadam
          qo'yilmagan": neytral ohang va ANIQ keyingi qadam.

          ⚠️ Ogohlantirish MATNI yo'qolmadi: `emptyHint` o'sha joyda
          qoladi va oqibatni aynan aytadi ("hozir bironta abonent
          ulana olmaydi"). Ya'ni yo'qotilgani faqat QIZIL RANG, ma'lumot
          emas — operator nima bo'layotganini baribir o'qiydi.
        */
        <EmptyState
          variant="no-device"
          title={t('dashboard.network.emptyTitle')}
          description={t('dashboard.network.emptyHint')}
          {...(canAdd
            ? { actionLabel: t('dashboard.network.emptyAction'), onAction: () => navigate('/nas') }
            : {})}
        />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {rows.map((device) => (
            <div
              key={device.nasId}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
            >
              <Space size={6}>
                <Badge status={problem(device.state) ? 'error' : device.state === 'IDLE' ? 'default' : 'success'} />
                <Typography.Text ellipsis style={{ maxWidth: 150 }}>
                  {device.shortname}
                </Typography.Text>
              </Space>
              {/*
                ⚠️ "Qachon oxirgi marta eshitildi" — holat tegidan MUHIMROQ.
                "Ishlamayapti" degan so'zning o'zi savol tug'diradi: qachondan
                beri? Javob shu yerda va u aniq harakatga olib boradi.
              */}
              <Tooltip title={t('dashboard.network.lastSeenHint')}>
                <Typography.Text
                  type={problem(device.state) ? 'danger' : 'secondary'}
                  style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  {relativeFromNow(device.lastContact, t)}
                </Typography.Text>
              </Tooltip>
            </div>
          ))}
        </Space>
      )}
    </Card>
  )
}
